// [PURPOSE]: Unified error-reporting helper. Wraps Sentry + Firebase Analytics + console
//            so caught errors (try/catch blocks) become visible in the same channels as
//            uncaught render crashes.
// [WHY]: Without this, every `try { ... } catch (e) { console.error(e) }` block is a
//        black hole — users hit silent bugs and we never learn about them. Sentry only
//        sees uncaught exceptions by default; this helper lets us promote caught ones.
// [INTEGRATION]: Import { reportError } from 'src/services/errorReporter'.
//                Call inside catch blocks alongside existing console.error / showStatus.
//                Safe to call even if Sentry/Analytics aren't initialized — silently no-ops.

import * as Sentry from '@sentry/react';
import { trackEvent } from './analytics';

/**
 * Report a caught error to Sentry + analytics + console.
 *
 * @param {Error|unknown} error — the caught error (any type, will be coerced)
 * @param {Object} [opts]
 * @param {string} [opts.area] — short label for the failing area (e.g. 'load_character',
 *                                'stripe_checkout', 'ai_portrait'). Used as a Sentry tag
 *                                AND as the analytics event suffix.
 * @param {Object} [opts.extra] — additional non-PII context (characterId, route, etc.)
 *                                Will be attached to the Sentry event as extras.
 * @param {boolean} [opts.silent] — if true, suppresses console.error (rare; default false)
 */
export const reportError = (error, opts = {}) => {
  const { area = 'unknown', extra = {}, silent = false } = opts;

  // [PURPOSE]: Always log to console so local devs still see it.
  if (!silent) {
    console.error(`[${area}]`, error, extra);
  }

  // [PURPOSE]: Send to Sentry with scoped tag + extras for triage.
  // [WHY]: Wrapping in try/catch — error reporting itself must never throw.
  try {
    Sentry.withScope((scope) => {
      scope.setTag('area', area);
      Object.entries(extra).forEach(([key, value]) => {
        try {
          scope.setExtra(key, value);
        } catch {
          // Skip un-serializable extras
        }
      });
      const errObj = error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : JSON.stringify(error));
      Sentry.captureException(errObj);
    });
  } catch {
    // Sentry reporting must not crash the app
  }

  // [PURPOSE]: Track in Firebase Analytics for aggregate error-rate metrics.
  // [WHY]: Sentry shows individual events; analytics shows trend lines.
  try {
    trackEvent('caught_error', {
      area,
      error_name: (error && error.name) || 'Error',
      error_message: ((error && error.message) || String(error)).slice(0, 100),
    });
  } catch {
    // Non-fatal
  }
};

export default reportError;
